import React, {createContext, useContext, useState, ReactNode} from 'react'
import {KTSVG} from '_metronic/helpers'
import {Divider} from 'antd'
import {Modal, Spinner} from 'react-bootstrap'
import clsx from 'clsx'
import {useFormik} from 'formik'
import {changePasswordPayload} from 'openapi'
import * as Yup from 'yup'
import api from 'config/api'
import {getUser} from 'app/modules/auth'
import {IsApiResponseError, IsErrorResponse} from 'app/core/_core'
import {MarvelToast} from 'app/helper/MarvelToast'

interface ModalContextType {
  showModal: (onConfirm?: () => Promise<void> | void) => void
  hideModal: () => void
}

interface Model {
  old_password?: string | null
  new_password?: string | null
  confirm_password?: string | null
}

const ModalContext = createContext<ModalContextType | undefined>(undefined)

export const ChangePasswordProvider = ({children}: {children: ReactNode}) => {
  const [isShow, setIsShow] = useState<boolean>(false)
  const [obscure, setObscure] = useState(true)
  const user = getUser()

  const showModal = (confirmCallback?: () => Promise<void> | void) => {
    setIsShow(true)
  }

  const hideModal = async () => {
    formik?.resetForm()
    setIsShow(false)
    MarvelToast.success('Password berhasil diubah')
  }

  const handleConfirm = async () => {
    formik?.handleSubmit()
  }

  const [temp] = useState<Model>({
    old_password: '',
    new_password: '',
    confirm_password: '',
  })

  const validation = Yup.object().shape({
    old_password: Yup.string().nullable().required('Password lama tidak boleh kosong.'),
    new_password: Yup.string().nullable().required('Password baru tidak boleh kosong.'),
    confirm_password: Yup.string()
      .nullable()
      .oneOf([Yup.ref('new_password'), null], 'Password konfirmasi harus sama dengan password baru.')
      .required('Konfirmasi password tidak boleh kosong.'),
  })

  var formik = useFormik<Model>({
    enableReinitialize: true,
    initialValues: temp,
    validationSchema: validation,
    validateOnBlur: false,
    validateOnChange: false,
    validateOnMount: false,
    onSubmit: async (values, {setSubmitting, resetForm, setFieldError}) => {
      setSubmitting(true)
      MarvelToast.loading()
      try {
        let payload: changePasswordPayload = {
          id: user?.id,
          old_password: values?.old_password ?? '',
          new_password: values?.new_password ?? '',
        }

        var r = (await api.user.postUserChangePassword({requestBody: payload})) as unknown
        if (r) await hideModal()
      } catch (r: any) {
        if (IsApiResponseError(r)) {
          MarvelToast.error(r?.error ?? '')
        } else if (IsErrorResponse(r)) {
          MarvelToast.error(r?.message ?? '')
        } else {
          MarvelToast.error('Ada sesuatu yang salah, Silahkan kontak Administrator')
        }
      } finally {
        MarvelToast.removeLoading()
        setSubmitting(false)
      }
    },
  })

  return (
    <ModalContext.Provider value={{showModal, hideModal}}>
      {children}
      <Modal show={isShow} onHide={hideModal} animation={false} centered className='ops-modal-confirm' backdrop='static' size='sm'>
        <Modal.Body className='p-0 py-5'>
          <fieldset disabled={formik.isSubmitting}>
            <div className='d-flex justify-content-between align-items-center ps-10 pe-7'>
              <h3 className='fw-bold mb-0' style={{letterSpacing: -0.5}}>
                Ubah Password
              </h3>
              <div className=''>
                <div className='btn btn-icon btn-sm btn-active-light-primary' onClick={hideModal}>
                  <KTSVG path='/media/icons/duotune/arrows/arr061.svg' className='svg-icon-2x' />
                </div>
              </div>
            </div>
            <Divider className='mt-4 mb-4' />
            <div className='px-10 py-5'>
              <fieldset disabled={false}>
                <div className='fv-row mb-10'>
                  <div className='d-flex justify-content-between mt-n5'>
                    <div className='d-flex flex-stack mb-2'>
                      <label className='form-label text-dark fs-6 mb-0'>Password Lama</label>
                    </div>
                  </div>
                  <div className='input-group mb-3'>
                    <input
                      type={`${obscure ? 'password' : 'text'}`}
                      {...formik.getFieldProps('old_password')}
                      className={clsx(
                        'form-control form-control-lg form-control-solid fw-normal rounded-0',
                        {
                          'is-invalid': formik.touched.old_password && formik.errors.old_password,
                        },
                        {
                          'is-valid': formik.touched.old_password && !formik.errors.old_password,
                        }
                      )}
                      placeholder='Masukkan Password Lama'
                      onKeyDown={() => formik.setFieldError('old_password', undefined)}
                      autoComplete='off'
                    />
                    <span className='input-group-text form-control-solid border-0' id='basic-addon2' onClick={() => setObscure(!obscure)}>
                      <i className={`fa ${obscure ? 'fa-eye ' : 'fa-eye-slash'} fs-1x `}></i>
                    </span>
                  </div>
                  {formik.touched.old_password && formik.errors.old_password && (
                    <div className='fv-plugins-message-container'>
                      <div className='fv-help-block'>
                        <span role='alert'>{formik.errors.old_password}</span>
                      </div>
                    </div>
                  )}
                </div>
                <div className='fv-row mb-10'>
                  <div className='d-flex justify-content-between mt-n5'>
                    <div className='d-flex flex-stack mb-2'>
                      <label className='form-label text-dark fs-6 mb-0'>Password Baru</label>
                    </div>
                  </div>
                  <div className='input-group mb-3'>
                    <input
                      type={`${obscure ? 'password' : 'text'}`}
                      {...formik.getFieldProps('new_password')}
                      className={clsx(
                        'form-control form-control-lg form-control-solid fw-normal rounded-0',
                        {
                          'is-invalid': formik.touched.new_password && formik.errors.new_password,
                        },
                        {
                          'is-valid': formik.touched.new_password && !formik.errors.new_password,
                        }
                      )}
                      placeholder='Masukkan Password Baru'
                      autoComplete='off'
                      onKeyDown={() => formik.setFieldError('new_password', undefined)}
                    />
                    <span className='input-group-text form-control-solid border-0' id='basic-addon2' onClick={() => setObscure(!obscure)}>
                      <i className={`fa ${obscure ? 'fa-eye ' : 'fa-eye-slash'} fs-1x `}></i>
                    </span>
                  </div>
                  {formik.touched.new_password && formik.errors.new_password && (
                    <div className='fv-plugins-message-container'>
                      <div className='fv-help-block'>
                        <span role='alert'>{formik.errors.new_password}</span>
                      </div>
                    </div>
                  )}
                </div>
                <div className='fv-row'>
                  <div className='d-flex justify-content-between mt-n5'>
                    <div className='d-flex flex-stack mb-2'>
                      <label className='form-label text-dark fs-6 mb-0'>Ulangi Password Baru</label>
                    </div>
                  </div>
                  <div className='input-group mb-0'>
                    <input
                      type={`${obscure ? 'password' : 'text'}`}
                      {...formik.getFieldProps('confirm_password')}
                      className={clsx(
                        'form-control form-control-lg form-control-solid fw-normal rounded-0',
                        {
                          'is-invalid': formik.touched.confirm_password && formik.errors.confirm_password,
                        },
                        {
                          'is-valid': formik.touched.confirm_password && !formik.errors.confirm_password,
                        }
                      )}
                      placeholder='Ulangi Password Baru'
                      autoComplete='off'
                      onKeyDown={() => formik.setFieldError('confirm_password', undefined)}
                    />
                    <span className='input-group-text form-control-solid border-0' id='basic-addon2' onClick={() => setObscure(!obscure)}>
                      <i className={`fa ${obscure ? 'fa-eye ' : 'fa-eye-slash'} fs-1x `}></i>
                    </span>
                  </div>
                  {formik.touched.confirm_password && formik.errors.confirm_password && (
                    <div className='fv-plugins-message-container'>
                      <div className='fv-help-block'>
                        <span role='alert'>{formik.errors.confirm_password}</span>
                      </div>
                    </div>
                  )}
                </div>
              </fieldset>
            </div>

            <Divider className='mt-0 mb-4' />
            <div className='marvel-modal-footer text-end px-10'>
              <button type='button' className='btn btn-primary btn-sm rounded-0 fs-6 px-10 me-2' onClick={handleConfirm} disabled={formik.isSubmitting}>
                {formik.isSubmitting ? (
                  <>
                    <Spinner animation='border' size='sm' role='status' aria-hidden='true' />
                    <span className='ms-2'>{'Sedang diproses...'}</span>
                  </>
                ) : (
                  'Ubah Password'
                )}
              </button>
              <button type='button' className='btn btn-danger btn-sm rounded-0 fs-6 px-7' onClick={hideModal} disabled={formik.isSubmitting}>
                Batal
              </button>
            </div>
          </fieldset>
        </Modal.Body>
      </Modal>
    </ModalContext.Provider>
  )
}

export const useChangePasswordModal = () => {
  const context = useContext(ModalContext)
  if (!context) {
    throw new Error('useModal must be used within a ModalProvider')
  }
  return context
}
